import { create } from "zustand";
import { produce } from "immer";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "../Central";
import { showNotification } from "../Central/selectors";
import { SupabaseWriteTableEnum } from "@/lib/supabase/supabaseTypes";
import { useCatalogsStore } from "../Catalogs";
import { useTemplatesStore } from "../Templates/templates.store";
import {
	ServiceCatalogItem,
	ServiceCatalogItemFilter,
	ServiceCatalogFolder,
	ServiceCatalogTree,
	EMPTY_CATALOG,
} from "./types";

function itemMatchesFn(
	item: ServiceCatalogItem,
	filter: ServiceCatalogItemFilter
): boolean {
	if (filter.tariffs_default_id)
		return item.tariffs_default_id === filter.tariffs_default_id;
	if (filter.tariffs_custom_id)
		return item.tariffs_custom_id === filter.tariffs_custom_id;
	if (filter.article_id) return item.article_id === filter.article_id;
	if (filter.template_id) return item.template_id === filter.template_id;
	return false;
}

function findFolderById(
	folders: ServiceCatalogFolder[],
	id: string
): ServiceCatalogFolder | null {
	for (const folder of folders) {
		if (folder.id === id) return folder;
		const found = findFolderById(folder.folders, id);
		if (found) return found;
	}
	return null;
}

function removeFolderById(
	folders: ServiceCatalogFolder[],
	id: string
): boolean {
	const idx = folders.findIndex((f) => f.id === id);
	if (idx !== -1) {
		folders.splice(idx, 1);
		return true;
	}
	for (const folder of folders) {
		if (removeFolderById(folder.folders, id)) return true;
	}
	return false;
}

function collectFolderNamesWithItem(
	folders: ServiceCatalogFolder[],
	filter: ServiceCatalogItemFilter
): string[] {
	const names: string[] = [];
	for (const folder of folders) {
		if (folder.items.some((item) => itemMatchesFn(item, filter))) {
			names.push(folder.name);
		}
		names.push(...collectFolderNamesWithItem(folder.folders, filter));
	}
	return names;
}

function removeItemFromFolders(
	folders: ServiceCatalogFolder[],
	filter: ServiceCatalogItemFilter
): void {
	for (const folder of folders) {
		folder.items = folder.items.filter(
			(item) => !itemMatchesFn(item, filter)
		);
		removeItemFromFolders(folder.folders, filter);
	}
}

function replaceItemInFolders(
	folders: ServiceCatalogFolder[],
	from: ServiceCatalogItemFilter,
	to: ServiceCatalogItem
): void {
	for (const folder of folders) {
		folder.items = folder.items.map((item) =>
			itemMatchesFn(item, from) ? { ...to } : item
		);
		replaceItemInFolders(folder.folders, from, to);
	}
}

function getItemSortInfo(
	item: ServiceCatalogItem,
	tariffsLookup: Record<string, Record<string, any>>,
	articles: Record<string, Record<string, any>>,
	templatesLookup: Record<number, any>
): { typeOrder: number; description: string } {
	if (item.tariffs_default_id || item.tariffs_custom_id) {
		const tariffId = item.tariffs_custom_id || item.tariffs_default_id;
		for (const codeEntries of Object.values(tariffsLookup)) {
			const tariff = tariffId ? codeEntries[tariffId] : undefined;
			if (tariff)
				return {
					typeOrder: 0,
					description: tariff.description_de || "",
				};
		}
		return { typeOrder: 0, description: "" };
	}
	if (item.article_id) {
		for (const codeEntries of Object.values(articles)) {
			const article = codeEntries[String(item.article_id)];
			if (article)
				return {
					typeOrder: 1,
					description: article.description_de || "",
				};
		}
		return { typeOrder: 1, description: "" };
	}
	if (item.template_id) {
		const template = templatesLookup[item.template_id];
		if (template) return { typeOrder: 2, description: template.name || "" };
		return { typeOrder: 2, description: "" };
	}
	return { typeOrder: 3, description: "" };
}

interface ServiceCatalogState {
	catalog: ServiceCatalogTree;
	isLoading: boolean;
	hasUnsavedChanges: boolean;

	loadCatalog: () => void;
	saveCatalog: () => Promise<void>;

	addFolder: (parentId: string | null, name: string) => void;
	renameFolder: (folderId: string, name: string) => void;
	deleteFolder: (folderId: string) => void;

	addItem: (
		folderId: string,
		item: ServiceCatalogItem,
		index?: number
	) => void;
	removeItem: (folderId: string, itemIndex: number) => void;
	moveItem: (
		fromFolderId: string,
		fromIndex: number,
		toFolderId: string,
		toIndex: number
	) => void;
	moveFolder: (
		folderId: string,
		newParentId: string | null,
		position: number
	) => void;

	sortFolderAlphabetically: (folderId: string) => void;
	reorderItems: (
		folderId: string,
		oldIndex: number,
		newIndex: number
	) => void;
	reorderFolders: (
		parentId: string | null,
		oldIndex: number,
		newIndex: number
	) => void;

	checkItemUsage: (filter: ServiceCatalogItemFilter) => string[];
	removeItemGlobally: (filter: ServiceCatalogItemFilter) => void;
	replaceItemGlobally: (
		from: ServiceCatalogItemFilter,
		to: ServiceCatalogItem
	) => void;
}

export const useServiceCatalogStore = create<ServiceCatalogState>()(
	(set, get) => ({
		catalog: EMPTY_CATALOG,
		isLoading: false,
		hasUnsavedChanges: false,

		loadCatalog: () => {
			const org = useCentralStore.getState().organization;
			if (!org) return;
			const raw = org.service_catalog as ServiceCatalogTree | null;
			set({
				catalog: raw && raw.tree ? raw : EMPTY_CATALOG,
				hasUnsavedChanges: false,
			});
		},

		saveCatalog: async () => {
			const orgId = useCentralStore.getState().organization?.id;
			if (!orgId) return;

			const { catalog } = get();
			const { error } = await supabase
				.from(SupabaseWriteTableEnum.ORGANIZATIONS)
				.update({ service_catalog: catalog as any })
				.eq("id", orgId);

			if (error) {
				Logger.error(error, {}, "Error saving service catalog");
				showNotification({
					message: "Fehler beim Speichern des Leistungskatalogs",
					type: "error",
				});
				return;
			}

			useCentralStore.setState(
				produce((state: any) => {
					if (state.organization) {
						state.organization.service_catalog = catalog;
					}
				})
			);

			set({ hasUnsavedChanges: false });
		},

		addFolder: (parentId, name) => {
			set(
				produce((state: ServiceCatalogState) => {
					const newFolder: ServiceCatalogFolder = {
						id: uuidv4(),
						name,
						folders: [],
						items: [],
					};
					if (parentId === null) {
						state.catalog.tree.push(newFolder);
					} else {
						const parent = findFolderById(
							state.catalog.tree,
							parentId
						);
						if (parent) parent.folders.push(newFolder);
					}
					state.hasUnsavedChanges = true;
				})
			);
		},

		renameFolder: (folderId, name) => {
			set(
				produce((state: ServiceCatalogState) => {
					const folder = findFolderById(state.catalog.tree, folderId);
					if (folder) folder.name = name;
					state.hasUnsavedChanges = true;
				})
			);
		},

		deleteFolder: (folderId) => {
			set(
				produce((state: ServiceCatalogState) => {
					removeFolderById(state.catalog.tree, folderId);
					state.hasUnsavedChanges = true;
				})
			);
		},

		addItem: (folderId, item, index?) => {
			set(
				produce((state: ServiceCatalogState) => {
					const folder = findFolderById(state.catalog.tree, folderId);
					if (folder) {
						if (index !== undefined) {
							folder.items.splice(index, 0, item);
						} else {
							folder.items.push(item);
						}
						state.hasUnsavedChanges = true;
					}
				})
			);
		},

		removeItem: (folderId, itemIndex) => {
			set(
				produce((state: ServiceCatalogState) => {
					const folder = findFolderById(state.catalog.tree, folderId);
					if (folder && itemIndex < folder.items.length) {
						folder.items.splice(itemIndex, 1);
						state.hasUnsavedChanges = true;
					}
				})
			);
		},

		moveItem: (fromFolderId, fromIndex, toFolderId, toIndex) => {
			set(
				produce((state: ServiceCatalogState) => {
					const from = findFolderById(
						state.catalog.tree,
						fromFolderId
					);
					const to = findFolderById(state.catalog.tree, toFolderId);
					if (!from || !to) return;
					if (fromIndex >= from.items.length) return;

					const [item] = from.items.splice(fromIndex, 1);
					to.items.splice(toIndex, 0, item);
					state.hasUnsavedChanges = true;
				})
			);
		},

		moveFolder: (folderId, newParentId, position) => {
			set(
				produce((state: ServiceCatalogState) => {
					const folder = findFolderById(state.catalog.tree, folderId);
					if (!folder) return;

					const folderCopy = {
						...folder,
						folders: [...folder.folders],
						items: [...folder.items],
					};
					removeFolderById(state.catalog.tree, folderId);

					if (newParentId === null) {
						state.catalog.tree.splice(position, 0, folderCopy);
					} else {
						const parent = findFolderById(
							state.catalog.tree,
							newParentId
						);
						if (parent) {
							parent.folders.splice(position, 0, folderCopy);
						}
					}
					state.hasUnsavedChanges = true;
				})
			);
		},

		sortFolderAlphabetically: (folderId) => {
			const tariffsLookup = useCatalogsStore.getState().tariffsLookup;
			const articles = useCentralStore.getState().articles;
			const templatesLookup =
				useTemplatesStore.getState().templatesLookup;

			set(
				produce((state: ServiceCatalogState) => {
					const folder = findFolderById(state.catalog.tree, folderId);
					if (!folder) return;

					folder.folders.sort((a, b) =>
						a.name.localeCompare(b.name, "de")
					);

					folder.items.sort((a, b) => {
						const infoA = getItemSortInfo(
							a,
							tariffsLookup,
							articles,
							templatesLookup
						);
						const infoB = getItemSortInfo(
							b,
							tariffsLookup,
							articles,
							templatesLookup
						);
						if (infoA.typeOrder !== infoB.typeOrder)
							return infoA.typeOrder - infoB.typeOrder;
						return infoA.description.localeCompare(
							infoB.description,
							"de"
						);
					});

					state.hasUnsavedChanges = true;
				})
			);
		},

		reorderItems: (folderId, oldIndex, newIndex) => {
			set(
				produce((state: ServiceCatalogState) => {
					const folder = findFolderById(state.catalog.tree, folderId);
					if (!folder) return;
					const [item] = folder.items.splice(oldIndex, 1);
					folder.items.splice(newIndex, 0, item);
					state.hasUnsavedChanges = true;
				})
			);
		},

		reorderFolders: (parentId, oldIndex, newIndex) => {
			set(
				produce((state: ServiceCatalogState) => {
					const arr =
						parentId === null
							? state.catalog.tree
							: findFolderById(state.catalog.tree, parentId)
									?.folders;
					if (!arr) return;
					const [folder] = arr.splice(oldIndex, 1);
					arr.splice(newIndex, 0, folder);
					state.hasUnsavedChanges = true;
				})
			);
		},

		checkItemUsage: (filter) => {
			return collectFolderNamesWithItem(get().catalog.tree, filter);
		},

		removeItemGlobally: (filter) => {
			set(
				produce((state: ServiceCatalogState) => {
					removeItemFromFolders(state.catalog.tree, filter);
					state.hasUnsavedChanges = true;
				})
			);
		},

		replaceItemGlobally: (from, to) => {
			set(
				produce((state: ServiceCatalogState) => {
					replaceItemInFolders(state.catalog.tree, from, to);
					state.hasUnsavedChanges = true;
				})
			);
		},
	})
);
